import { TranslateService } from '@shared/translate/translate.service';
import { ProductData } from '@types';
import { ColDef, ColGroupDef } from '@ag-grid-community/core';
import { AgGridHelperService } from '../../services/ag-grid-helper.service';
import { HeaderRendererComponent } from '@frk/eds-components';
import { EMDASH } from '@products/utils/constants/product.constants';
import { PpssRatingsComponent } from '@products/fund-listing/ppss/ppss-ratings/ppss-ratings.component';

/**
 * Return default identifier column definitions for Canada.
 * @param agGridHelperService ag grid helper service instance
 * @param translateService translate service instance
 */
export function getDefaultIdentifierColumnDefsForCA(
  agGridHelperService: AgGridHelperService,
  translateService: TranslateService
): ColDef[] {
  const columnDefs = [
    agGridHelperService.getFundNameColDef(true),
    {
      colId: 'CURRENCY_CODE',
      headerName: translateService.instant('pds.MARKET_CURRENCY'),
      headerComponentFramework: HeaderRendererComponent,
      valueGetter: (params) => {
        const product: ProductData = params.data;
        return product.shareClass?.shareClassCurrency;
      },
      width: 88,
      autoHeight: true,
      wrapText: true,
      suppressMovable: true,
    },
    {
      colId: 'FUND_CODE',
      // headerName: translateService.instant('pds.FUND_CODE'), // Check with PDS Label
      headerName: translateService.instant('products.ppss-fi-fund-code'),
      headerComponentFramework: HeaderRendererComponent,
      headerComponentParams: {
        footnotePlacement: `PPSS:${agGridHelperService.getConfigurationNameForPlacement()}FundCodePPSS`,
      },
      valueGetter: (params) => {
        const product: ProductData = params.data;
        return product.shareClass?.identifiers.taid
          ? product.shareClass?.identifiers.taid
          : EMDASH;
      },
      width: 88,
      autoHeight: true,
      wrapText: true,
      suppressMovable: true,
    },
    {
      colId: 'FRONT_END',
      // headerName: translateService.instant('pds.FRONT_END'), // Check with PDS Label
      headerName: translateService.instant('products.ppss-fi-front-end'),
      headerComponentFramework: HeaderRendererComponent,
      headerComponentParams: {
        footnotePlacement: `PPSS:${agGridHelperService.getConfigurationNameForPlacement()}FrontEndPPSS`,
      },
      valueGetter: (params) => {
        const product: ProductData = params.data;
        return product.shareClass?.identifiers.frontend
          ? product.shareClass?.identifiers.frontend
          : EMDASH;
      },
      width: 88,
      autoHeight: true,
      wrapText: true,
      suppressMovable: true,
    },
    {
      colId: 'LOW_LOAD',
      // headerName: translateService.instant('pds.LOW_LOAD'), // Check with PDS Label
      headerName: translateService.instant('products.ppss-fi-low-load'),
      headerComponentFramework: HeaderRendererComponent,
      headerComponentParams: {
        footnotePlacement: `PPSS:${agGridHelperService.getConfigurationNameForPlacement()}LowLoadPPSS`,
      },
      valueGetter: (params) => {
        const product: ProductData = params.data;
        return product.shareClass?.identifiers.lowload
          ? product.shareClass?.identifiers.lowload
          : EMDASH;
      },
      width: 88,
      autoHeight: true,
      wrapText: true,
      suppressMovable: true,
    },
    {
      colId: 'DSC',
      // headerName: translateService.instant('pds.DSC'), // Check with PDS Label
      headerName: translateService.instant('products.ppss-fi-dsc'),
      headerComponentFramework: HeaderRendererComponent,
      headerComponentParams: {
        footnotePlacement: `PPSS:${agGridHelperService.getConfigurationNameForPlacement()}DscPPSS`,
      },
      valueGetter: (params) => {
        const product: ProductData = params.data;
        return product.shareClass?.identifiers.dsc
          ? product.shareClass?.identifiers.dsc
          : EMDASH;
      },
      width: 88,
      autoHeight: true,
      wrapText: true,
      suppressMovable: true,
    },
    {
      colId: 'ADM',
      // headerName: translateService.instant('pds.ADM'), // Check with PDS Label
      headerName: translateService.instant('products.ppss-fi-adm'),
      headerComponentFramework: HeaderRendererComponent,
      headerComponentParams: {
        footnotePlacement: `PPSS:${agGridHelperService.getConfigurationNameForPlacement()}AdmPPSS`,
      },
      valueGetter: (params) => {
        const product: ProductData = params.data;
        return product.shareClass?.identifiers.adm
          ? product.shareClass?.identifiers.adm
          : EMDASH;
      },
      width: 88,
      autoHeight: true,
      wrapText: true,
      suppressMovable: true,
    },
  ];
  return columnDefs;
}

/**
 * Return default rating column definitions for intl.
 * @param ppssTab ppss tab component
 * @param agGridHelperService ag grid helper service instance
 */
export function getDefaultRatingColumnDefsForCA(
  ppssTab: PpssRatingsComponent,
  agGridHelperService: AgGridHelperService
): ColGroupDef[] {
  return [
    {
      groupId: 'FUND',
      children: [agGridHelperService.getFundNameColDef(true)],
    },
    {
      groupId: 'CURRENCY',
      children: [agGridHelperService.getCurrencyColDef()],
    },
    {
      groupId: 'MORNINGSTAR',
      headerGroupComponent: 'ratingsGroupHeaderComponent',
      headerClass: 'ag-header-group-cell--noflex',
      headerGroupComponentParams: {
        ratingsAsOfDate: ppssTab.ratingsAsOfDate,
        isSiteIntl: ppssTab.isSiteIntl,
      },
      children: [
        agGridHelperService.getMorningStarCategoryColDef(),
        agGridHelperService.getMorningStarOverallRatingColDef(false),
        agGridHelperService.getMorningStar3yrRatingColDef(),
        agGridHelperService.getMorningStar5yrRatingColDef(),
        agGridHelperService.getMorningStar10yrRatingColDef(),
      ],
    },
  ];
}
